import { AtomStateManagerProvider } from '@common/AtomStateManagerProvider';
import { useAllConnectionSpec } from '@common/Fabric/Connections/hooks';
import { OrchestrationConnectionType } from '@common/Fabric/Connections/types';
import { useOrchestrationDataplaneApis } from '@common/Fabric/Connections/useOrchestrationDataplaneApis';
import { useOrchestrationFabricConnections } from '@common/Fabric/Connections/useOrchestrationFabricConnections';
import { FabricContextProvider } from '@common/Fabric/OrchestrationSecrets/FarbricIdProvider';
import { OrchestrationDefaultWarehouseConnection } from '@common/Fabric/types/orchestration';
import { QueryKeys } from '@common/queries/common';
import { additionalComponentsForOrchConnection } from '@common/sqlIde/fabric/connections/CreateConnectionDialog';
import { COMMON_ACTION_TYPES, CommonGraph, CommonState, EditMode } from '@common/types';
import { UISpecElement } from '@common/types/specTypes';
import { theme } from '@prophecy/theme';
import { Button, Centered, Stack, Text } from '@prophecy/ui-v3';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useRestMutation } from 'frontend/core/src/data/util';
import { useMemo, useRef, useState } from 'react';
import { Store } from 'redux';
import styled from 'styled-components';

import { Parser } from '../../Parser';
import { ProviderFormProvider, useProviderForm } from './common/ProviderFormContext';
import { extractKGCard } from './utils/extractKGCard';

const Container = styled(Stack)`
  max-width: 1200px;
  padding: ${theme.spaces.x24};
  margin: 0 auto;
`;

const SaveButton = styled(Button)`
  align-self: flex-end;
` as unknown as typeof Button;
const EmptyStateContainer = styled(Centered)`
  flex-direction: column;
  gap: ${theme.spaces.x16};
  padding: ${theme.spaces.x64} ${theme.spaces.x24};
`;

type IndexerTabProps = {
  fabricId: string;
  teamId: string;
};

export function IndexerTab({ fabricId, teamId }: IndexerTabProps) {
  const connectionSpecs = useAllConnectionSpec();
  const { connections, isLoading: isLoadingConnections } = useOrchestrationFabricConnections();

  // and only show the main connection (default warehouse connection)
  const defaultWarehouseConnection = useMemo(() => {
    return connections.find((conn) => conn.isDefaultWarehouseConnection) as
      | OrchestrationDefaultWarehouseConnection
      | undefined;
  }, [connections]);

  const kgSpecInfo = useMemo(() => {
    if (!defaultWarehouseConnection) {
      return null;
    }
    const connectionSpec = connectionSpecs.find(
      (s) => s.kind === (defaultWarehouseConnection.kind as unknown as OrchestrationConnectionType)
    );

    if (!connectionSpec || !connectionSpec?.uiSpec) {
      return null;
    }

    return extractKGCard(connectionSpec.uiSpec);
  }, [connectionSpecs, defaultWarehouseConnection]);

  if (isLoadingConnections) {
    return (
      <Centered>
        <Spinner size='l' spinning />
      </Centered>
    );
  }

  if (!defaultWarehouseConnection) {
    return (
      <EmptyStateContainer>
        <Text level='lg' weight='semibold'>
          No SQL Warehouse Connection Found
        </Text>
        <Text level='md' tone={theme.colors.gray600} style={{ textAlign: 'center', maxWidth: '600px' }}>
          To configure the Knowledge Graph Indexer, you need to set up a SQL warehouse connection in the Providers tab
          first.
        </Text>
      </EmptyStateContainer>
    );
  }

  if (connectionSpecs.length === 0 || !kgSpecInfo) {
    return (
      <EmptyStateContainer>
        <Text level='lg' weight='semibold'>
          Knowledge Graph Indexer Not Available
        </Text>
        <Text level='md' tone={theme.colors.gray600} style={{ textAlign: 'center', maxWidth: '600px' }}>
          The Knowledge Graph Indexer configuration is not available for this connection type. Please ensure you have a
          compatible warehouse connection configured.
        </Text>
      </EmptyStateContainer>
    );
  }

  return (
    <ProviderFormProvider isOrchestrationFabric={true} teamId={teamId}>
      <Container gap={theme.spaces.x20}>
        <Stack gap={theme.spaces.x16}>
          <ConnectionKGConfig connection={defaultWarehouseConnection} kgSpec={kgSpecInfo} fabricId={fabricId} />
        </Stack>
      </Container>
    </ProviderFormProvider>
  );
}

// Component to render KG config for a single connection
function ConnectionKGConfig({
  connection,
  kgSpec,
  fabricId
}: {
  connection: OrchestrationDefaultWarehouseConnection;
  kgSpec: UISpecElement;
  fabricId: string;
}) {
  const storeRef = useRef<Store<CommonState, COMMON_ACTION_TYPES<CommonGraph>, {}>>();
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();
  const { updateConnection } = useOrchestrationDataplaneApis();
  const { readonly, readonlyMessage } = useProviderForm();

  const { mutateAsync: _updateConnection, isLoading: isSaving } = useRestMutation(updateConnection, {
    sentryTag: 'indexer-updateConnection'
  });

  const onPropertiesDidChange = usePersistentCallback(() => {
    setIsDirty(true);
  });

  const onStoreMount = usePersistentCallback((store: Store<CommonState, COMMON_ACTION_TYPES<CommonGraph>, {}>) => {
    storeRef.current = store;
  });

  const handleSave = usePersistentCallback(async () => {
    if (!storeRef.current) return;

    try {
      const state = storeRef.current.getState() as CommonState;
      const updatedProperties = state.currentGraph?.processes?.[state.currentComponentId ?? '']?.properties;

      if (!updatedProperties) {
        toast.error({ content: 'Failed to get updated properties' });
        return;
      }

      await _updateConnection({
        name: connection.name,
        kind: connection.kind,
        properties: updatedProperties as Record<string, unknown>,
        isDefaultWarehouseConnection: connection.isDefaultWarehouseConnection
      });

      toast.success({ content: `Saved KG configuration for ${connection.name}` });
      setIsDirty(false);

      // Invalidate queries to refresh connection list
      queryClient.invalidateQueries([QueryKeys.EnvironmentConnections]);
    } catch (error) {
      toast.error({ content: `Failed to save: ${(error as Error).message}` });
    }
  });

  const stateProps = useMemo(() => {
    return {
      rootPath: '$.connections',
      initialProcess: {
        id: connection.id,
        component: connection.kind,
        name: connection.name,
        kind: connection.kind,
        properties: connection.properties
      }
    };
  }, [connection]);

  return (
    <Stack gap={theme.spaces.x20}>
      <FabricContextProvider fabricId={fabricId}>
        <AtomStateManagerProvider
          stateProps={stateProps}
          storeRef={storeRef}
          ideMode={readonly ? EditMode.disabled : EditMode.enabled}
          disabledMessage={readonlyMessage}
          additionalComponents={additionalComponentsForOrchConnection}
          onPropertiesDidChange={onPropertiesDidChange}
          onStoreMount={onStoreMount}>
          <Parser specs={kgSpec} />
        </AtomStateManagerProvider>
      </FabricContextProvider>

      <SaveButton
        variant='primary'
        onClick={handleSave}
        loading={isSaving}
        disabled={!isDirty || isSaving || readonly}
        data-test-id={`kg-indexer-save-${connection.name}-button`}>
        Save Configuration
      </SaveButton>
    </Stack>
  );
}
